import { LocalizedText, LocalizedElement, sourceAttribute, useSourceLanguage } from "../i18n/LocalizedCopy";
import React, { useEffect, useRef, useState } from "react";
import {
  applyPwaUpdate,
  dismissInstallSuggestion,
  dismissPwaUpdate,
  getPwaCapabilities,
  installEternuaApp,
  markPwaInstallVisit,
  markInstallSuggestionShown,
  recordPwaLifecycleEvent,
  subscribeInstallAvailability,
  subscribePwaUpdate,
} from "./pwa";
import "./PwaRuntime.css";

const INSTALL_SUGGESTION_DELAY_MS = 45_000;

export default function PwaRuntime() {
  useSourceLanguage();
  const [online, setOnline] = useState(() => getPwaCapabilities().online);
  const [installState, setInstallState] = useState(() => getPwaCapabilities());
  const [showInstall, setShowInstall] = useState(false);
  const [update, setUpdate] = useState({ available: false, error: null });
  const [message, setMessage] = useState("");
  const wasVisited = useRef(null);

  useEffect(() => {
    wasVisited.current = markPwaInstallVisit();
    if (getPwaCapabilities().standalone) recordPwaLifecycleEvent("standalone_launch");
    const unsubscribeInstall = subscribeInstallAvailability((available, capabilities) => {
      setInstallState({ ...capabilities, installPromptAvailable: available });
    });
    const unsubscribeUpdate = subscribePwaUpdate(setUpdate);
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      unsubscribeInstall();
      unsubscribeUpdate();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const browserCanPrompt = installState.installPromptAvailable;
    const iosCanExplain = installState.iosSafari && wasVisited.current;
    if (
      installState.standalone
      || !installState.installSuggestionAllowed
      || (!browserCanPrompt && !iosCanExplain)
    ) {
      setShowInstall(false);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setShowInstall(true);
      markInstallSuggestionShown();
    }, INSTALL_SUGGESTION_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [installState]);

  const closeInstall = () => {
    dismissInstallSuggestion();
    setShowInstall(false);
  };

  const install = async () => {
    const result = await installEternuaApp();
    if (result.accepted) setShowInstall(false);
  };

  const updateNow = async () => {
    const result = await applyPwaUpdate();
    if (result.blocked) {
      setMessage("Enregistrez ou abandonnez vos modifications avant de mettre Eternua à jour.");
    } else if (!result.applied) {
      setMessage("La mise à jour n’est plus disponible. Eternua est déjà à jour.");
    }
  };

  return (
    <div className="pwa-runtime" aria-live="polite">
      {!online && (
        <div className="pwa-runtime__offline" role="status">
          <span aria-hidden="true">●</span><LocalizedText source={"Vous êtes hors ligne. Connexion nécessaire pour terminer cette action."} /></div>
      )}

      {update.available && (
        <section className="pwa-runtime__notice" aria-labelledby="pwa-update-title">
          <div>
            <strong id="pwa-update-title"><LocalizedText source={"Une nouvelle version d’Eternua est prête"} /></strong>
            <p><LocalizedText source={"La page sera rechargée après votre confirmation."} /></p>
          </div>
          <div className="pwa-runtime__actions">
            <button type="button" className="pwa-runtime__primary" onClick={updateNow}><LocalizedText source={"Mettre à jour"} /></button>
            <button
              type="button"
              className="pwa-runtime__secondary"
              onClick={() => dismissPwaUpdate()}
            ><LocalizedText source={"Plus tard"} /></button>
          </div>
        </section>
      )}

      {showInstall && (
        <section className="pwa-runtime__notice" aria-labelledby="pwa-install-title">
          <img src="/icons/icon-192.png" width="48" height="48" alt="" />
          <div>
            <strong id="pwa-install-title"><LocalizedText source={"Installer Eternua"} /></strong>
            {installState.installPromptAvailable ? (
              <p><LocalizedText source={"Accédez plus vite à Eternua depuis votre écran d’accueil."} /></p>
            ) : (
              <p><LocalizedText source={"Sur Safari, touchez Partager puis « Sur l’écran d’accueil »."} /></p>
            )}
          </div>
          <div className="pwa-runtime__actions">
            {installState.installPromptAvailable && (
              <button type="button" className="pwa-runtime__primary" onClick={install}><LocalizedText source={"Installer"} /></button>
            )}
            <button type="button" className="pwa-runtime__secondary" onClick={closeInstall}><LocalizedText source={"Pas maintenant"} /></button>
          </div>
        </section>
      )}

      {(message || update.error) && (
        <div className="pwa-runtime__message" role="alert">
          <span>{<LocalizedText source={message || update.error} />}</span>
          <LocalizedElement elementType={"button"} copyAttributes={["aria-label"]} type="button" onClick={() => { setMessage(""); setUpdate(current => ({ ...current, error: null })); }} aria-label={sourceAttribute("Fermer ce message")}>×</LocalizedElement>
        </div>
      )}
    </div>
  );
}
